import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Filter, Building2, MapPin, GraduationCap, Clock, Tag, TrendingUp, Briefcase, Calendar, ExternalLink, Mail, Globe, X, Copy, Check } from 'lucide-react'

// 投递链接弹窗组件
const ApplyLinkModal = ({ job, onClose }) => {
  const [copied, setCopied] = useState(false)

  if (!job) return null

  const handleCopyEmail = () => {
    if (job.applyEmail) {
      navigator.clipboard.writeText(job.applyEmail)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* 头部 */}
          <div className="bg-primary-50 px-6 py-4 border-b border-primary-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{job.company}</h3>
                  <p className="text-sm text-gray-500">{job.position}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors">
                <X size={20} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* 内容 */}
          <div className="px-6 py-5 space-y-4">
            {/* 投递邮箱 */}
            {job.applyEmail && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail size={18} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">投递邮箱</span>
                </div>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-lg text-sm text-gray-700 font-mono border border-blue-200">
                    {job.applyEmail}
                  </code>
                  <button
                    onClick={handleCopyEmail}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 ${
                      copied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {copied ? (
                      <><Check size={14} /><span>已复制</span></>
                    ) : (
                      <><Copy size={14} /><span>复制</span></>
                    )}
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  请将简历发送至该邮箱，邮件标题注明：应聘{job.position}-{job.company}
                </p>
              </div>
            )}

            {/* 官网投递 */}
            {job.applyUrl && (
              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Globe size={18} className="text-green-600" />
                  <span className="text-sm font-medium text-green-900">官网投递</span>
                </div>
                <a
                  href={job.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between w-full px-4 py-3 bg-white rounded-lg border border-green-200 hover:border-green-400 hover:bg-green-50 transition-colors group"
                >
                  <span className="text-sm text-gray-700 truncate mr-2">{job.applyUrl}</span>
                  <span className="flex items-center text-sm font-medium text-green-700 group-hover:text-green-800 whitespace-nowrap">
                    前往投递
                    <ExternalLink size={14} className="ml-1" />
                  </span>
                </a>
              </div>
            )}

            {/* 信息来源 */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">信息来源：{job.source}</span>
                <span className="text-gray-400">
                  <Eye size={12} className="inline mr-1" />
                  {job.views}次浏览
                </span>
              </div>
            </div>
          </div>

          {/* 底部 */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              关闭
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

const JobsPage = () => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ keyword: '', location: '', degree: '', tag: '', sortBy: 'deadline' })
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, pages: 0 })
  const [filterOptions, setFilterOptions] = useState({})
  const [stats, setStats] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)

  // 记录浏览次数
  const recordView = async (jobId) => {
    try {
      await fetch(`/api/jobs/${jobId}/view`, { method: 'POST' })
    } catch (e) {
      console.error('记录浏览失败:', e)
    }
  }

  const handleOpenApplyModal = (job) => {
    recordView(job.id)
    setSelectedJob(job)
  }

  const fetchJobs = async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(), limit: '15',
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.location && { location: filters.location }),
        ...(filters.degree && { degree: filters.degree }),
        ...(filters.tag && { tag: filters.tag }),
        sortBy: filters.sortBy
      })
      const response = await fetch(`/api/jobs?${params}`)
      const data = await response.json()
      if (data.status === 200) {
        setJobs(data.data.jobs)
        setPagination(data.data.pagination)
        setFilterOptions(data.data.filters)
      }
    } catch (error) {
      console.error('获取招聘信息失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/jobs/stats/overview')
      const data = await response.json()
      if (data.status === 200) setStats(data.data)
    } catch (error) {
      console.error('获取统计失败:', error)
    }
  }

  useEffect(() => { fetchJobs(); fetchStats() }, [])

  useEffect(() => {
    const timer = setTimeout(() => { fetchJobs(1) }, 300)
    return () => clearTimeout(timer)
  }, [filters.keyword])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    fetchJobs(1)
  }

  const getDeadlineColor = (deadline) => {
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24))
    if (days <= 3) return 'text-red-600 bg-red-50'
    if (days <= 7) return 'text-orange-600 bg-orange-50'
    return 'text-green-600 bg-green-50'
  }

  const getDeadlineText = (deadline) => {
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24))
    if (days < 0) return '已截止'
    if (days === 0) return '今天截止'
    if (days <= 7) return `${days}天后截止`
    return deadline
  }

  const hasApplyLink = (job) => job.applyEmail || job.applyUrl

  return (
    <div className="max-w-7xl mx-auto">
      {/* 弹窗 */}
      {selectedJob && <ApplyLinkModal job={selectedJob} onClose={() => setSelectedJob(null)} />}

      {/* 页面标题 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <Briefcase className="text-primary-600" size={32} />
        </div>
        <h1 className="section-title">央国企招聘信息</h1>
        <p className="section-subtitle">自动采集各大平台校招信息，每日更新</p>
      </motion.div>

      {/* 统计卡片 */}
      {stats && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Building2 size={20} className="text-primary-600" />
              <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
            </div>
            <p className="text-sm text-gray-600">在招岗位</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Clock size={20} className="text-red-600" />
              <span className="text-2xl font-bold text-red-600">{stats.deadlineSoon}</span>
            </div>
            <p className="text-sm text-gray-600">即将截止</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Calendar size={20} className="text-green-600" />
              <span className="text-2xl font-bold text-green-600">{stats.thisWeek}</span>
            </div>
            <p className="text-sm text-gray-600">本周新增</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={20} className="text-blue-600" />
              <span className="text-2xl font-bold text-blue-600">{stats.byType?.校招 || 0}</span>
            </div>
            <p className="text-sm text-gray-600">校招岗位</p>
          </div>
        </motion.div>
      )}

      {/* 搜索和筛选 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="搜索公司、职位、专业..." value={filters.keyword}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter size={18} className="mr-2 text-gray-600" />
            <span className="text-sm text-gray-700">筛选</span>
          </button>
        </div>

        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">工作地点</label>
              <select value={filters.location} onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">全部地点</option>
                {filterOptions.locations?.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">学历要求</label>
              <select value={filters.degree} onChange={(e) => handleFilterChange('degree', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">全部学历</option>
                {filterOptions.degrees?.map(deg => <option key={deg} value={deg}>{deg}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">岗位标签</label>
              <select value={filters.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">全部标签</option>
                {filterOptions.tags?.map(tag => <option key={tag} value={tag}>{tag}</option>)}
              </select>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* 排序 */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          共 <span className="font-medium text-gray-900">{pagination.total}</span> 条招聘信息
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">排序：</span>
          <select value={filters.sortBy} onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="deadline">截止日期的近到远</option>
            <option value="salary">薪资从高到低</option>
            <option value="views">热度从高到低</option>
          </select>
        </div>
      </div>

      {/* 招聘信息表格 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">加载中...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">暂无符合条件的招聘信息</p>
          </div>
        ) : (
          <>
            {/* 桌面端表格 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">公司/职位</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">工作地点</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">学历/专业</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">薪资</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">截止日期</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">标签</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {jobs.map((job, index) => (
                    <motion.tr key={job.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                            <Building2 size={18} className="text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{job.company}</p>
                            <p className="text-sm text-gray-500">{job.position}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin size={14} className="mr-1 text-gray-400" />{job.location}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          <div className="flex items-center text-gray-600 mb-1">
                            <GraduationCap size={14} className="mr-1 text-gray-400" />{job.degree}
                          </div>
                          <p className="text-gray-500 text-xs">{job.major}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-medium text-green-600">{job.salary}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getDeadlineColor(job.deadline)}`}>
                          <Clock size={12} className="mr-1" />{getDeadlineText(job.deadline)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {job.tags.map(tag => (
                            <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary-50 text-primary-700">
                              <Tag size={10} className="mr-1" />{tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {hasApplyLink(job) ? (
                          <button
                            onClick={() => handleOpenApplyModal(job)}
                            className="inline-flex items-center px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors"
                          >
                            <ExternalLink size={14} className="mr-1" />
                            投递链接
                          </button>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                            <ExternalLink size={14} className="mr-1" />
                            暂无链接
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 移动端卡片 */}
            <div className="md:hidden divide-y divide-gray-100">
              {jobs.map((job, index) => (
                <motion.div key={job.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                        <Building2 size={18} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{job.company}</p>
                        <p className="text-sm text-gray-500">{job.position}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDeadlineColor(job.deadline)}`}>
                      {getDeadlineText(job.deadline)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div className="flex items-center text-gray-600">
                      <MapPin size={14} className="mr-1 text-gray-400" />{job.location}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <GraduationCap size={14} className="mr-1 text-gray-400" />{job.degree}
                    </div>
                    <div className="flex items-center text-green-600 font-medium">{job.salary}</div>
                    <div className="text-gray-500 text-xs">{job.major}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {job.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary-50 text-primary-700">{tag}</span>
                      ))}
                    </div>
                    {hasApplyLink(job) ? (
                      <button
                        onClick={() => handleOpenApplyModal(job)}
                        className="inline-flex items-center px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors"
                      >
                        <ExternalLink size={14} className="mr-1" />
                        投递链接
                      </button>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-sm">
                        <ExternalLink size={14} className="mr-1" />
                        暂无链接
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 分页 */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center mt-6 space-x-2">
          <button onClick={() => fetchJobs(pagination.page - 1)} disabled={pagination.page <= 1}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >上一页</button>
          <span className="text-sm text-gray-600">第 {pagination.page} / {pagination.pages} 页</span>
          <button onClick={() => fetchJobs(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >下一页</button>
        </div>
      )}

      <div className="mt-8 p-4 bg-gray-50 rounded-xl">
        <p className="text-sm text-gray-500 text-center">
          数据来源：国聘网、国资委官网、中智招聘、智联招聘等 |
          最后更新：{stats?.lastCrawlTime ? new Date(stats.lastCrawlTime).toLocaleString('zh-CN') : '刚刚'}
        </p>
      </div>
    </div>
  )
}

export default JobsPage
